/**
 * main-tile-mapping.ts — Plan 05-12 Task 1.
 *
 * Single source of truth mapping MAIN-NN → { component, section }.
 * Decisions derived per UI-SPEC Tile-family decision rules table + xlsx Notes column.
 *
 * TileComponent values:
 *   'trend'       → MainTrendTile (single big-number + sparkline)
 *   'bucketed'    → MainBucketedTile (ECharts stacked-bar/area/line)
 *   'table'       → MainTableTile (sortable grid with SF deep-links)
 *   'placeholder' → MainPlaceholderTile (Card chrome + 2-line muted text)
 *
 * SectionKey values per tab grouping:
 *   Sales Activity tab:
 *     'activity'   → ACTIVITY section
 *     'pipeline'   → PIPELINE section
 *     'efficiency' → PIPELINE EFFICIENCY section
 *   Marketing tab:
 *     'inbound'      → INBOUND LEADS section
 *     'mktpipeline'  → MARKETING PIPELINE & EFFICIENCY section
 *     'phase2'       → Campaign Metrics Phase 2 (collapsed details)
 *   ROI tab:
 *     'roi-efficiency' → EFFICIENCY section
 *     'roi-channel'    → BY-CHANNEL ROI section
 *     'roi-quota'      → QUOTA section
 */

export type TileComponent = 'trend' | 'bucketed' | 'table' | 'placeholder' | 'treemap' | 'bar' | 'pacing';

export type SectionKey =
  | 'headline'
  | 'activity'
  | 'pipeline'
  | 'efficiency'
  | 'inbound'
  | 'mktpipeline'
  | 'mktanalytics'
  | 'phase2'
  | 'roi-efficiency'
  | 'roi-channel'
  | 'roi-quota';

export type ChartType = 'sparkline' | 'stackedBar' | 'area' | 'lineByQuarter' | 'dailyBar' | 'weeklyBar';

/** Variant for 'bar' component tiles (quick-260626-cot Task 3.3). */
export type BarVariant = 'horizontalBar' | 'progress';

/** Value unit for horizontalBar tiles, drives axis + label formatting. */
export type BarValueUnit = 'percent' | 'days' | 'currency' | 'number';

export interface TileMappingEntry {
  component: TileComponent;
  section: SectionKey;
  /** Chart type for bucketed tiles; undefined for trend/table/placeholder */
  chartType?: ChartType;
  /** Variant for 'bar' component tiles (horizontalBar / progress). */
  barVariant?: BarVariant;
  /** Value unit for horizontalBar tiles (percent / days / currency / number). */
  barValueUnit?: BarValueUnit;
  /**
   * Optional 12-column grid span (1–12) for sections that opt into a fixed
   * 12-col grid (currently only the ACTIVITY section — quick-260626-g28 Task 3).
   * Below `lg` the tile collapses to full width regardless; at `lg+` it occupies
   * `span` of 12 columns. Sections that don't opt in ignore this field and keep
   * their auto-fit behavior.
   */
  span?: number;
  /** Verbatim xlsx Notes field for debugging / audit trail */
  xlsxNotes?: string;
}

export const TILE_COMPONENT_VALUES: TileComponent[] = ['trend', 'bucketed', 'table', 'placeholder', 'treemap', 'bar', 'pacing'];
export const TILE_SECTION_VALUES: SectionKey[] = [
  'headline',
  'activity',
  'pipeline',
  'efficiency',
  'inbound',
  'mktpipeline',
  'mktanalytics',
  'phase2',
  'roi-efficiency',
  'roi-channel',
  'roi-quota',
];

/**
 * MAIN_TILE_MAPPING — canonical mapping for the buildable MAIN-NN IDs
 * + 6 Campaign Metrics Phase 2 placeholder entries.
 *
 * Per Plan 05-12 must_have truth: all MAIN-NN in BUILDABLE_MAIN_METRIC_IDS must
 * appear here — EXCEPT the ids in RETIRED_MAIN_METRIC_IDS (below), which the
 * backend still builds but the board no longer shows.
 */
export const MAIN_TILE_MAPPING: Record<string, TileMappingEntry> = {
  // ─── ACTIVITY section (Sales Activity tab) ───────────────────────────────

  // MAIN-01: New Meetings Booked — weekly count bar chart (quick-260630-wiw,
  // was a single trend tile). Identical in shape to MAIN-04 Outbound: X =
  // calendar-week start ('M/D'), Y = new-meetings-booked count over the last 30
  // days (~5 weekly bars). Headline = 30-day total (carried as grouped.total_value).
  // Backed by agg_main_scorecard_grouped. Draws a dashed 120/week target markLine
  // (see WEEKLY_BAR_TARGET in MainBucketedTile). Stays in the Sales-tab Headline
  // band (quick-260626-cot Task 2.2), now a chart.
  'MAIN-01': { component: 'bucketed', section: 'headline', chartType: 'weeklyBar', span: 3, xlsxNotes: 'Weekly new meetings booked, last 30 days; headline = 30-day total' },

  // MAIN-03: Opportunities Created — bucketed (grouped by type/source).
  // Activity-section row D: full 12-col row (quick-260626-g28 Task 3).
  'MAIN-03': { component: 'bucketed', section: 'activity', chartType: 'stackedBar', span: 12, xlsxNotes: 'Bar Graph. Stacked by type' },

  // MAIN-04: Outbound Activity — weekly count bar chart (quick-260626-j5u Task 3,
  // was a single trend tile). X = calendar-week start ('M/D'), Y = completed-
  // activity count over the last 30 days (~5 weekly bars). Headline = 30-day
  // total (carried as grouped.total_value). Backed by agg_main_scorecard_grouped.
  // Stays in the Sales-tab Headline band (quick-260626-cot Task 2.2), now a chart.
  'MAIN-04': { component: 'bucketed', section: 'headline', chartType: 'weeklyBar', span: 3, xlsxNotes: 'Weekly outbound activity, last 30 days; headline = 30-day total' },

  // MAIN-05/06/07: Email / Call / LinkedIn Activity — weekly count bar chart
  // (quick-260626-g28 Task 2 → weekly per quick-260626-j5u Task 3). X = calendar-
  // week start ('M/D'), Y = completed-activity count over the last 30 days
  // (~5 weekly bars). Headline = 30-day total (carried as grouped.total_value).
  // Backed by agg_main_scorecard_grouped. Activity-section row A: 3 across (span 4).
  'MAIN-05': { component: 'bucketed', section: 'activity', chartType: 'weeklyBar', span: 4, xlsxNotes: 'Weekly email activity, last 30 days; headline = 30-day total' },
  'MAIN-06': { component: 'bucketed', section: 'activity', chartType: 'weeklyBar', span: 4, xlsxNotes: 'Weekly call activity, last 30 days; headline = 30-day total' },
  'MAIN-07': { component: 'bucketed', section: 'activity', chartType: 'weeklyBar', span: 4, xlsxNotes: 'Weekly LinkedIn activity, last 30 days; headline = 30-day total' },

  // MAIN-08 / MAIN-10 / MAIN-46 / MAIN-47 (the lead-response band) intentionally
  // absent — REMOVED from the board 2026-09-21 (quick-260921-m88, John). See
  // RETIRED_MAIN_METRIC_IDS below.

  // MAIN-09 intentionally absent (deferred per PLAN.md + api-contracts.ts comment)

  // ─── PIPELINE section (Sales Activity tab) ───────────────────────────────

  // MAIN-11: Discovery Deals >$200k — treemap (quick-260626-cot Task 3.1).
  // Each open deal a node sized by Amount, colored by stage, click → SF record.
  // Replaces the MainTableTile grid; reuses the same rich bucket shape
  // ({ oppId, name, amount, stage, accountName, ownerName }).
  'MAIN-11': { component: 'treemap', section: 'pipeline', xlsxNotes: 'Open deals >$200k treemap, sized by amount, colored by stage' },

  // MAIN-12: Avg $$ / Opportunity — bucketed line by quarter (QoQ + YoY over the
  // trailing 5 fiscal quarters; quick-260625-hz9 Task 4).
  'MAIN-12': { component: 'bucketed', section: 'pipeline', chartType: 'lineByQuarter', xlsxNotes: 'Avg opp size, trailing 5 fiscal quarters (QoQ + YoY)' },

  // MAIN-13..18: Open Pipeline/Mature (CQ/NQ/FY) — trend (snap-backed)
  'MAIN-13': { component: 'trend', section: 'pipeline', xlsxNotes: 'Trended snapshot — pipeline CQ' },
  'MAIN-14': { component: 'trend', section: 'pipeline', xlsxNotes: 'Trended snapshot — mature CQ' },
  'MAIN-15': { component: 'trend', section: 'pipeline', xlsxNotes: 'Trended snapshot — pipeline NQ' },
  'MAIN-16': { component: 'trend', section: 'pipeline', xlsxNotes: 'Trended snapshot — mature NQ' },
  // MAIN-17: Open Pipeline FY — hero tile in the Sales-tab Headline band
  // (quick-260626-cot Task 2.2). Now renders the cumulative-vs-target PACING
  // chart (Task 3.2): the tile reads metrics[MAIN-17] (headline + FY target) AND
  // the grouped[MAIN-48] monthly cumulative series. Featured/wider tile.
  'MAIN-17': { component: 'pacing', section: 'headline', span: 3, xlsxNotes: 'Cumulative-vs-target pacing chart (open pipeline FY by close month) + $8.7M FY target line' },
  'MAIN-18': { component: 'trend', section: 'pipeline', xlsxNotes: 'Trended snapshot — mature FY' },

  // MAIN-19: Opps Advanced / week — bucketed (stacked bar by stage-change date by week)
  'MAIN-19': { component: 'bucketed', section: 'pipeline', chartType: 'stackedBar', xlsxNotes: 'Grouped by stage change date by week' },

  // MAIN-48: Open Pipeline FY pacing SERIES (quick-260626-cot Task 3.2).
  // Data-only companion to MAIN-17 — the pacing tile (rendered under MAIN-17 in
  // the headline band) reads grouped['MAIN-48'] for its monthly cumulative
  // series. MAIN-48 has a mapping entry only so the "every buildable id has a
  // mapping" contract test passes; it is NOT rendered as its own tile (excluded
  // from every section's tile-id list via COMPANION_SERIES_IDS).
  'MAIN-48': { component: 'pacing', section: 'headline', xlsxNotes: 'Companion series for MAIN-17 pacing chart (not rendered standalone)' },

  // ─── PIPELINE EFFICIENCY section (Sales Activity tab) ────────────────────

  // PIPELINE EFFICIENCY section renders as a 12-col span grid (quick-260626-j5u
  // Task 6), explicit row packing via EFFICIENCY_TILE_IDS order + per-tile span:
  //   Row 1: MAIN-24 Win Rate + MAIN-21 Stalled Deals  (span 6 each)
  //   Row 2: MAIN-22 Lead→Meeting + MAIN-23 Lead→Opp    (span 6 each)
  //   Row 3: MAIN-20 Sales Cycle + MAIN-26 Deal Slippage (span 6 each)
  //   Row 4: MAIN-25 Loss by Reason                      (span 12, full row)

  // MAIN-20: Sales Cycle Length — bucketed line by quarter (QoQ + YoY over the
  // trailing 5 fiscal quarters; quick-260625-hz9 Task 5).
  'MAIN-20': { component: 'bucketed', section: 'efficiency', chartType: 'lineByQuarter', span: 6, xlsxNotes: 'Avg sales cycle days, trailing 5 fiscal quarters (QoQ + YoY)' },

  // MAIN-21: Stalled Deals — bucketed bars BY CURRENT STAGE (2026-07-22, John).
  // Rebuilt from a snap-backed trend line to a by-stage bar chart: stage duration
  // > 21d AND next-step-modified > 21d, counted per StageName. The WoW pill still
  // rides the total-count daily snapshot (threaded via snapData in MainTileSection).
  'MAIN-21': { component: 'bucketed', section: 'efficiency', chartType: 'stackedBar', span: 6, xlsxNotes: 'Stalled deals by current stage; stage duration >21d AND next step untouched >21d' },

  // MAIN-22: Lead → Meeting % — trend
  'MAIN-22': { component: 'trend', section: 'efficiency', span: 6, xlsxNotes: 'Show trend of lead to meeting rate' },

  // MAIN-23: Lead → Opp % — trend
  'MAIN-23': { component: 'trend', section: 'efficiency', span: 6, xlsxNotes: 'Show trend of lead to opp rate' },

  // MAIN-24: Win Rate % — trend
  'MAIN-24': { component: 'trend', section: 'efficiency', span: 6, xlsxNotes: 'Show trend of win rate' },

  // MAIN-25: Loss Rate by Reason — bucketed (stacked bar by reason). Full row.
  'MAIN-25': { component: 'bucketed', section: 'efficiency', chartType: 'stackedBar', span: 12, xlsxNotes: 'Summarize by stacked columns with reason trends digestible' },

  // MAIN-26: Deal Slippage — bucketed bar by month the CloseDate push occurred
  // (slipped Amount + count), rebuilt on OpportunityFieldHistory
  // (quick-260625-hz9 Task 8).
  'MAIN-26': { component: 'bucketed', section: 'efficiency', chartType: 'stackedBar', span: 6, xlsxNotes: 'Monthly slipped amount + count of opps with pushed CloseDate (field history)' },

  // ─── INBOUND LEADS section (Marketing tab) ───────────────────────────────

  // MAIN-27: Inbound Leads by Status — bucketed (stacked bar)
  'MAIN-27': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Stacked by Status' },

  // MAIN-28: Inbound Leads by Source — bucketed (stacked bar)
  'MAIN-28': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Stacked by Source' },

  // MAIN-29: Inbound Leads by Industry — bucketed (stacked bar)
  'MAIN-29': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Stacked by Industry' },

  // MAIN-30: Inbound Working Leads by Owner — bucketed (stacked bar)
  'MAIN-30': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Stacked by Owner' },

  // MAIN-31: Disqualified Leads by Reason — bucketed (stacked bar)
  'MAIN-31': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Stacked by Reason' },

  // MAIN-45: Leads with activity in last month, by owner — bucketed (stacked bar)
  // NEW (quick-260625-hz9 Task 13): inbound leads with >= 1 Task/Event in the
  // last 30 days, grouped by Lead Owner, sorted desc.
  'MAIN-45': { component: 'bucketed', section: 'inbound', chartType: 'stackedBar', xlsxNotes: 'Bar Graph. Inbound leads with activity in last 30d, by owner' },

  // ─── MARKETING PIPELINE & EFFICIENCY section (Marketing tab) ─────────────

  // MAIN-49: Marketing-Sourced Win Rate, trended (2026-07-22, John — marketing
  // effectiveness). Per-fiscal-quarter won/(won+lost) for marketing-sourced
  // Product opps over the trailing 5 completed quarters, rendered as a percent
  // line-by-quarter so the DIRECTION of marketing effectiveness is visible (the
  // rest of the tab is descriptive slicing). Placed FIRST in the section so the
  // "is it working?" signal leads. Headline = pooled win rate across the window.
  'MAIN-49': { component: 'bucketed', section: 'mktpipeline', chartType: 'lineByQuarter', xlsxNotes: 'Marketing-sourced win rate by fiscal quarter (trailing 5 completed); headline = pooled win rate' },

  // MAIN-32: Mkt-Sourced Pipeline by Channel — bucketed (area graph)
  'MAIN-32': { component: 'bucketed', section: 'mktpipeline', chartType: 'area', xlsxNotes: 'Area Graph by channel' },

  // MAIN-33: Marketing Pipeline Coverage — bucketed stacked bar by CreatedDate
  // month, stacked by StageName (quick-260625-hz9 Task 12). Coverage total
  // equals the MAIN-32 marketing-sourced total.
  'MAIN-33': { component: 'bucketed', section: 'mktpipeline', chartType: 'stackedBar', xlsxNotes: 'Stacked bar by CreatedDate month, stacked by stage; total = MAIN-32 mkt-sourced' },

  // MAIN-50: Time in Stage — Marketing vs Non-Marketing (closed-won). Stacked-
  // by-stage bar, X = cohort; each segment = avg days in that stage, so the bar
  // height = avg total cycle and you compare BOTH speed and where time is spent
  // (2026-07-22, John — marketing effectiveness). Reuses the MAIN-33 stage-stack
  // rendering (buckets carry a `stages` map).
  'MAIN-50': { component: 'bucketed', section: 'mktpipeline', chartType: 'stackedBar', xlsxNotes: 'Avg days in each stage, Marketing vs Non-Marketing (closed-won); stacked by stage' },

  // MAIN-51: Time in Stage — Top 3 Sources (closed-won). Same stacked-by-stage
  // shape, one bar per top LeadSource (label carries n), so you compare how fast
  // each source's deals move through the funnel (2026-07-22, John).
  'MAIN-51': { component: 'bucketed', section: 'mktpipeline', chartType: 'stackedBar', xlsxNotes: 'Avg days in each stage for the top 3 sources (closed-won); stacked by stage' },

  // ─── MARKETING ANALYTICS section (Marketing tab — collapsed details) ─────
  // quick-260625-hz9 Task 14: MAIN-34/35/36 moved from 'mktpipeline' into a
  // deferred, collapsed "Marketing Analytics" <details> block (compute unchanged).

  // MAIN-34: Lead → Opp % by Channel — horizontal bars sorted desc (percent).
  // quick-260626-cot Task 3.3: table → bar (no tables).
  'MAIN-34': { component: 'bar', section: 'mktanalytics', barVariant: 'horizontalBar', barValueUnit: 'percent', xlsxNotes: 'Lead→Opp % by channel, horizontal bars sorted desc' },

  // MAIN-35: Win Rates on Mkt Lead by Channel — horizontal bars (percent).
  'MAIN-35': { component: 'bar', section: 'mktanalytics', barVariant: 'horizontalBar', barValueUnit: 'percent', xlsxNotes: 'Win rate by channel, horizontal bars sorted desc' },

  // MAIN-36: Sales Cycle Length Mkt by channel — horizontal bars (days).
  'MAIN-36': { component: 'bar', section: 'mktanalytics', barVariant: 'horizontalBar', barValueUnit: 'days', xlsxNotes: 'Avg sales cycle days by channel, horizontal bars sorted desc' },

  // ─── ROI EFFICIENCY section (ROI tab) ────────────────────────────────────

  // MAIN-37: Marketing Spend % of CAC — trend
  'MAIN-37': { component: 'trend', section: 'roi-efficiency', xlsxNotes: 'Finance-sourced — Marketing Spend % of CAC' },

  // MAIN-38: CAC — trend (Finance-sourced)
  'MAIN-38': { component: 'trend', section: 'roi-efficiency', xlsxNotes: 'Finance-sourced — CAC' },

  // MAIN-39: LTV : CAC — trend
  'MAIN-39': { component: 'trend', section: 'roi-efficiency', xlsxNotes: 'Finance-sourced — LTV:CAC ratio' },

  // MAIN-40: SE Spend to CAC — trend (optional — placeholder if blank)
  'MAIN-40': { component: 'trend', section: 'roi-efficiency', xlsxNotes: 'Finance-sourced — SE Spend to CAC (optional)' },

  // MAIN-41: AE Spend to CAC — trend (optional — placeholder if blank)
  'MAIN-41': { component: 'trend', section: 'roi-efficiency', xlsxNotes: 'Finance-sourced — AE Spend to CAC (optional)' },

  // ─── ROI BY-CHANNEL section (ROI tab) ─────────────────────────────────────

  // MAIN-42: Cost per Lead by Channel — horizontal bars sorted desc (currency).
  // quick-260626-cot Task 3.3: table → bar.
  'MAIN-42': { component: 'bar', section: 'roi-channel', barVariant: 'horizontalBar', barValueUnit: 'currency', xlsxNotes: 'Cost per lead by channel, horizontal bars sorted desc' },

  // MAIN-43: Cost per Opp by Channel — horizontal bars sorted desc (currency).
  'MAIN-43': { component: 'bar', section: 'roi-channel', barVariant: 'horizontalBar', barValueUnit: 'currency', xlsxNotes: 'Cost per opp by channel, horizontal bars sorted desc' },

  // ─── ROI QUOTA section (ROI tab) ─────────────────────────────────────────

  // MAIN-44: AE Actual vs Quota — per-AE progress-to-target bars.
  // quick-260626-cot Task 3.3: table → progress bars (tick at 100% of quota).
  'MAIN-44': { component: 'bar', section: 'roi-quota', barVariant: 'progress', xlsxNotes: 'AE actual vs quota, per-AE progress-to-target bars' },

  // ─── Campaign Metrics Phase 2 (Marketing tab — collapsed section) ────────

  'CAMPAIGN-PHASE2-1': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign 1 Leads / Opps / L→O% — Phase 2 (deferred, 6 tiles)',
  },
  'CAMPAIGN-PHASE2-2': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign 2 Leads / Opps / L→O% — Phase 2 (deferred, 6 tiles)',
  },
  'CAMPAIGN-PHASE2-3': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign Metrics Phase 2 — placeholder 3',
  },
  'CAMPAIGN-PHASE2-4': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign Metrics Phase 2 — placeholder 4',
  },
  'CAMPAIGN-PHASE2-5': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign Metrics Phase 2 — placeholder 5',
  },
  'CAMPAIGN-PHASE2-6': {
    component: 'placeholder',
    section: 'phase2',
    xlsxNotes: 'Campaign Metrics Phase 2 — placeholder 6',
  },
};

// ─── Per-section tile ID arrays (consumed by MainTileSection + MainScorecardPage) ──

/**
 * Data-only companion series that have a MAIN_TILE_MAPPING entry (to satisfy the
 * "every buildable id has a mapping" contract test) but are NOT rendered as
 * standalone tiles — their data is consumed by another tile. MAIN-48 feeds the
 * MAIN-17 pacing chart (quick-260626-cot Task 3.2).
 */
const COMPANION_SERIES_IDS = new Set<string>(['MAIN-48']);

/**
 * RETIRED tiles — still in BUILDABLE_MAIN_METRIC_IDS (the recompute runs, the API
 * serves them, the daily snaps accrue) but deliberately NOT mapped, so they never
 * reach a tab and the Summary readout never narrates them. Removed from the board
 * 2026-09-21 (quick-260921-m88, John: "don't reference the current lead response
 * tiles. remove them.") — the former Sales-tab lead-response band:
 *   MAIN-08  New Leads Needing Action
 *   MAIN-47  Lead Response Time — any response, incl. auto-responder
 *   MAIN-10  Human Lead Response Time
 *   MAIN-46  Inbound Leads with No Human Follow-up (60d)
 * The mapping contract test subtracts this set. Tearing the backend series down
 * (contract, registry, SOQL, definitions.json) is a separate decision.
 */
export const RETIRED_MAIN_METRIC_IDS = new Set<string>([
  'MAIN-08', 'MAIN-47', 'MAIN-10', 'MAIN-46',
]);

/** Filters mapping entries by section, returning metric IDs in definition order. */
function idsForSection(...sections: SectionKey[]): string[] {
  return Object.entries(MAIN_TILE_MAPPING)
    .filter(([k, v]) => sections.includes(v.section) && !COMPANION_SERIES_IDS.has(k))
    .map(([k]) => k);
}

/**
 * Sales-tab Headline band (quick-260626-cot Task 2.2). Explicit order — the
 * 3 hero metrics execs check first: Open Pipeline FY, Outbound Activity, New
 * Meetings Booked. These IDs are mapped to section 'headline' (moved out of
 * activity/pipeline) so they render once, here. (New Meetings Delivered /
 * MAIN-02 was removed as redundant with Booked — quick-260630-wiw.)
 */
export const HEADLINE_TILE_IDS = ['MAIN-17', 'MAIN-04', 'MAIN-01'];

/**
 * ACTIVITY section tiles in EXPLICIT row order (quick-260626-g28 Task 3). The
 * section renders as a 12-col grid; the per-tile `span` (above) + this order
 * yield exactly:
 *   Row A: MAIN-05 / MAIN-06 / MAIN-07   (span 4 each → 12)
 *   Row B: MAIN-03                       (span 12 → full row)
 * (The former rows B/C — the MAIN-08/47 and MAIN-10/46 lead-response band — were
 * removed 2026-09-21, quick-260921-m88; see RETIRED_MAIN_METRIC_IDS.)
 * Asserted against idsForSection('activity') below so a forgotten/extra
 * activity tile fails the contract test rather than silently dropping.
 */
export const SALES_ACTIVITY_TILE_IDS = [
  'MAIN-05', 'MAIN-06', 'MAIN-07',
  'MAIN-03',
];
export const PIPELINE_TILE_IDS = idsForSection('pipeline');

/**
 * PIPELINE EFFICIENCY section tiles in EXPLICIT row order (quick-260626-j5u
 * Task 6). The section renders as a 12-col grid; the per-tile `span` (above) +
 * this order yield exactly:
 *   Row 1: MAIN-24 (Win Rate) / MAIN-21 (Stalled Deals)   (span 6 each → 12)
 *   Row 2: MAIN-22 (Lead→Meeting) / MAIN-23 (Lead→Opp)    (span 6 each → 12)
 *   Row 3: MAIN-20 (Sales Cycle) / MAIN-26 (Deal Slippage) (span 6 each → 12)
 *   Row 4: MAIN-25 (Loss by Reason)                        (span 12 → full row)
 * Asserted against idsForSection('efficiency') in the contract test so a
 * forgotten/extra efficiency tile fails rather than silently dropping.
 */
export const EFFICIENCY_TILE_IDS = [
  'MAIN-24', 'MAIN-21',
  'MAIN-22', 'MAIN-23',
  'MAIN-20', 'MAIN-26',
  'MAIN-25',
];

export const INBOUND_TILE_IDS = idsForSection('inbound');
export const MKTPIPELINE_TILE_IDS = idsForSection('mktpipeline');
/** Deferred Marketing Analytics tiles (MAIN-34/35/36) — collapsed details. */
export const MKTANALYTICS_TILE_IDS = idsForSection('mktanalytics');
export const PHASE2_TILE_IDS = idsForSection('phase2');

export const ROI_EFFICIENCY_TILE_IDS = idsForSection('roi-efficiency');
export const ROI_CHANNEL_TILE_IDS = idsForSection('roi-channel');
export const ROI_QUOTA_TILE_IDS = idsForSection('roi-quota');

/** All Sales Activity tab tile IDs (headline band + activity + pipeline + efficiency) */
export const ALL_SALES_TILE_IDS = idsForSection('headline', 'activity', 'pipeline', 'efficiency');
/** All buildable Marketing tile IDs (inbound + mktpipeline + mktanalytics) + 6 phase-2 placeholders */
export const ALL_MARKETING_TILE_IDS = idsForSection('inbound', 'mktpipeline', 'mktanalytics', 'phase2');
/** All 8 ROI tile IDs */
export const ALL_ROI_TILE_IDS = idsForSection('roi-efficiency', 'roi-channel', 'roi-quota');

/**
 * SKO baseline comparison targets (quick-260630-f7f Task 8 item 15; filled
 * quick-260630-icb Task 4).
 *
 * MAIN-22 (Lead→Meeting %) and MAIN-23 (Lead→Opp %) are compared against the
 * baseline targets from the SKO deck. The values are now FILLED:
 *   - MAIN-22 = 0.058 → Lead→Meeting 5.8%
 *   - MAIN-23 = 0.018 → Lead→Opp 1.8%
 * Stored as 0–1 ratios. The MainTrendTile renders a "vs SKO baseline X%" chip
 * automatically now that the values are non-null (the null-gated "TBD (pending
 * SKO deck)" placeholder no longer fires).
 *
 * NOTE: the chip rounds to whole percent via `(skoBaseline * 100).toFixed(0)` —
 * 5.8% → "6%" and 1.8% → "2%". That rounding is expected, not a bug.
 */
export const SKO_BASELINE: Record<string, number | null> = {
  'MAIN-22': 0.058,
  'MAIN-23': 0.018,
};
